/**
 * Where a person is on `/learn` — issue #1128 (D221 O2).
 *
 * The page had ten `?tab=` values (a Training tab, an Assigned tab and eight
 * topic tabs). It now has two sections, My training and Guides:
 *
 *   /learn?section=training
 *   /learn?section=guides                 the Guides face: search, Assigned to you, topics
 *   /learn?section=guides&topic=batting   one topic's guides
 *
 * THE OLD `?tab=` VALUES STILL RESOLVE, for one release. Notification links
 * (`api/src/lib/notificationLinks.ts`), the ribbon, the Dashboard and the
 * journeys all carry `/learn?tab=…`, and a bookmark outlives a release. The
 * mapping, read here and nowhere else:
 *
 *   tab=training   ->  section=training
 *   tab=assigned   ->  section=guides, the assigned list scrolled into view
 *   tab=<topic>    ->  section=guides&topic=<topic>    (any of the eight)
 *   any other tab  ->  ignored (the default section)
 *
 * `section=drills` (#1159, round 12) is the coach's Drill bank. It has no
 * `?tab=` alias. The page reads it for a player as My training (the player has
 * no such section).
 *
 * `section` wins when both are present. A reader never rewrites the URL: the
 * old value is read as it stands, and the first choice a person makes writes
 * `section` (and drops `tab`) through `learnSearch`.
 *
 * `chosen` is what the person ASKED for, `null` on a bare `/learn` (#464: only
 * an explicit ask may clear notifications). It is the key of
 * `SURFACE_NOTIFICATION_TYPES` ("learn:training", "learn:assigned") so landing
 * on the Guides face, which lists what a coach assigned, clears the same
 * `coach_guide` / `coach_drill` items the Assigned tab did.
 */
export const LEARN_SECTIONS = ["training", "drills", "guides"];
export const DEFAULT_SECTION = "training";

/** The eight guide topics, in the order the Guides list shows them. */
export const LEARN_TOPIC_KEYS = [
  "batting",
  "bowling",
  "fielding",
  "wicketkeeping",
  "field-placement",
  "fitness",
  "nutrition",
  "mindset",
];

const isTopic = (v) => LEARN_TOPIC_KEYS.includes(v);

/**
 * `{ section, topic, chosen, assigned }` from the three URL params the page
 * reads, `{ section, tab, topic }` (each a string or `null`, straight from
 * `searchParams.get`). `topic` is only ever set in the guides section.
 * `assigned` is true when the person arrived by the old `?tab=assigned` link,
 * so the page can scroll the assigned list into view.
 */
export function readLearnLocation({ section: rawSection = null, tab: rawTab = null, topic: rawTopic = null } = {}) {

  if (rawSection === "training") return { section: "training", topic: null, chosen: "training", assigned: false };
  if (rawSection === "drills") return { section: "drills", topic: null, chosen: "drills", assigned: false };
  if (rawSection === "guides") {
    const topic = isTopic(rawTopic) ? rawTopic : null;
    return { section: "guides", topic, chosen: topic ? topic : "assigned", assigned: false };
  }

  if (rawTab === "training") return { section: "training", topic: null, chosen: "training", assigned: false };
  if (rawTab === "assigned") return { section: "guides", topic: null, chosen: "assigned", assigned: true };
  if (isTopic(rawTab)) return { section: "guides", topic: rawTab, chosen: rawTab, assigned: false };

  return { section: DEFAULT_SECTION, topic: null, chosen: null, assigned: false };
}

/**
 * The next search params after choosing a section or a topic: `section` (and
 * `topic`) are written, the legacy `tab` is dropped, and every other param
 * (`new=1`, `player=…`) is kept. Pass `topic: null` for the Guides face.
 */
export function learnSearch(current, { section, topic = null }) {
  const next = new URLSearchParams(current);
  next.delete("tab");
  next.set("section", section);
  if (section === "guides" && topic) next.set("topic", topic);
  else next.delete("topic");
  return next;
}

/** The path a guide's Back should return to for a topic: `/learn?section=guides&topic=<topic>`. */
export function learnTopicHref(topic) {
  return isTopic(topic) ? `/learn?section=guides&topic=${encodeURIComponent(topic)}` : "/learn?section=guides";
}
